<template>
    <div class="search-object-to-link">
        <CompassFormItem
            :label="$t.__('activities.labels.find_and_link_object')"
            class="search-object-to-link__search-input"
        >
            <CompassInput
                ref="searchInput"
                v-model="query"
                isEraseable
                :placeholder="$t.__('common.actions.search')"
                prefixIcon="search"
                @input="handleQueryInput"
            />
        </CompassFormItem>
        <CompassEmptyState
            v-if="!query"
            :title="emptyStateData.title"
            :caption="emptyStateData.caption"
            :illustrationPath="emptyStateData.illustrationPath"
        />
        <template v-else>
            <SearchBarFilters
                :filtersList="['company', 'contact', 'opportunity', 'document', 'individual']"
                :currentFilter="currentFilter"
                :permissions="permissions"
                @change="handleFilterChange"
            />
            <div v-if="isLoading">
                <div
                    v-for="index in 3"
                    :key="index"
                    class="result-container__loader typographies-paragraph-medium-regular"
                >
                    <CompassSkeleton variant="square" class="result-container__loader-image" />
                    <CompassSkeleton :lines="2" class="result-container__loader-text" />
                </div>
            </div>

            <div v-else-if="resultsCount === 0" class="result-container__no-results">
                <CompassIcon size="small" icon="search-no-result" />
                <p class="typographies-paragraph-medium-regular">
                    {{ $t.__('common.labels.no_result') }}
                </p>
            </div>

            <template v-else>
                <SearchResultsCompany
                    v-for="company in searchResults.companies.items"
                    :key="`company_${company.id}`"
                    v-bind="company"
                    context="link-to-activity"
                    link="javascript:void(0)"
                    @click="handleClickItem('company', company.type, company)"
                />
                <SearchResultsContact
                    v-for="contact in searchResults.contacts.items"
                    :key="`contact_${contact.id}`"
                    v-bind="contact"
                    context="link-to-activity"
                    link="javascript:void(0)"
                    @click="handleClickItem('contact', 'contact', contact)"
                />
                <SearchResultsOpportunity
                    v-for="opportunity in searchResults.opportunities.items"
                    :key="`opportunity_${opportunity.id}`"
                    v-bind="opportunity"
                    context="link-to-activity"
                    link="javascript:void(0)"
                    @click="handleClickItem('opportunity', 'opportunity', opportunity)"
                />
                <SearchResultsIndividual
                    v-for="individual in searchResults.individuals.items"
                    :key="`individual_${individual.id}`"
                    v-bind="individual"
                    context="link-to-activity"
                    link="javascript:void(0)"
                    @click="handleClickItem('individual', individual.type, individual)"
                />
                <SearchResultsDocument
                    v-for="document in searchResults.documents.items"
                    :key="`document_${document.id}`"
                    v-bind="document"
                    context="link-to-activity"
                    link="javascript:void(0)"
                    @click="handleClickItem('document', document.sub_type, document)"
                />
            </template>
        </template>
    </div>
</template>
<script>
import { search } from 'js/vue/api/searchEngineService';
import SearchResultsCompany from 'vcomponents/searchbar/results/SearchResultsCompany.vue';
import SearchResultsContact from 'vcomponents/searchbar/results/SearchResultsContact.vue';
import SearchResultsDocument from 'vcomponents/searchbar/results/SearchResultsDocument.vue';
import SearchResultsOpportunity from 'vcomponents/searchbar/results/SearchResultsOpportunity.vue';
import SearchResultsIndividual from 'vcomponents/searchbar/results/SearchResultsIndividual.vue';
import SearchBarFilters from 'vcomponents/searchbar/SearchBarFilters.vue';
import CompanyAdapter from 'Adapters/SearchEngineBeta/CompanyAdapter';
import ContactAdapter from 'Adapters/SearchEngineBeta/ContactAdapter';
import IndividualAdapter from 'Adapters/SearchEngineBeta/IndividualAdapter';
import OpportunityAdapter from 'Adapters/SearchEngineBeta/OpportunityAdapter';
import DocumentAdapter from 'Adapters/SearchEngineBeta/DocumentAdapter';
import { debounce } from 'lodash';
import { createNamespacedHelpers } from 'vuex';
const user = createNamespacedHelpers('user');

export default {
    components: {
        SearchResultsCompany,
        SearchResultsContact,
        SearchResultsDocument,
        SearchResultsOpportunity,
        SearchResultsIndividual,
        SearchBarFilters,
    },
    props: {
        isVisible: {
            type: Boolean,
            default: true,
        },
        emptyStateData: {
            type: Object,
            default: () => {
                return {
                    title: '',
                    caption: '',
                    illustrationPath: '',
                };
            },
        },
    },
    data() {
        return {
            isLoading: true,
            query: '',
            controller: new AbortController(),
            searchResults: {
                companies: [],
                contacts: [],
                opportunities: [],
                documents: [],
                individuals: [],
            },
            currentFilter: [],
            resultsCount: 0,
            response: [],
        };
    },
    computed: {
        ...user.mapState({
            permissions: (state) => state.permissions.searchBarFilter,
        }),
    },
    watch: {
        isVisible: {
            handler(newValue) {
                if (newValue) {
                    this.$refs.searchInput?.setFocus();
                } else {
                    this.query = '';
                }
            },
            immediate: true,
        },
    },
    mounted() {
        setTimeout(() => {
            this.$refs.searchInput?.setFocus();
        }, 0);
    },
    methods: {
        handleClickItem(blockType, type, item) {
            try {
                const data = this.response.find(
                    ({ object }) =>
                        object.id === item.id &&
                        object.type === blockType &&
                        object.sub_type === type,
                );
                data.type = type;
                data.status = item.status;
                this.$emit('objectSelected', { blockType, type, item, data });
            } catch (error) {
                this.$store.state.system.errorsHandler.logException(error);
            }
        },
        handleFilterChange(filter) {
            this.currentFilter = filter;
            for (const key in this.searchResults) {
                this.searchResults[key] = [];
            }
            this.handleQueryInput();
        },
        getFilters() {
            const docsFilters = [
                'document.estimate',
                'document.invoice',
                'document.order',
                'document.creditnote',
            ];
            if (this.currentFilter.length === 0) {
                return ['company', 'contact', 'opportunity', 'individual'].concat(docsFilters);
            }
            if (this.currentFilter[0] === 'document') {
                return docsFilters;
            }
            return this.currentFilter;
        },
        handleQueryInput: debounce(async function () {
            this.isLoading = true;
            this.resultsCount = 0;
            this.response = [];

            if (this.query.length > 0) {
                this.controller.abort();
                this.controller = new AbortController();

                try {
                    const res = await search({
                        query: this.query,
                        type: this.getFilters(),
                        signal: this.controller.signal,
                    });
                    this.response = res.data.data;
                    await this.populateSearchResults(res.data);

                    this.resultsCount = res.data.pagination.total;
                } catch (error) {
                    if (error.name === 'AbortError') return;
                    this.$store.state.system.errorsHandler.logException(error);
                    this.resultsCount = 0;
                } finally {
                    this.isLoading = false;
                }
            }
        }, 300),
        async populateSearchResults(data) {
            this.searchResults.companies = await new CompanyAdapter(data, this.query).build();
            this.searchResults.individuals = await new IndividualAdapter(data, this.query).build();
            this.searchResults.contacts = await new ContactAdapter(data, this.query).build();
            this.searchResults.opportunities = await new OpportunityAdapter(
                data,
                this.query,
            ).build();
            this.searchResults.documents = await new DocumentAdapter(data, this.query).build();
        },
    },
};
</script>

<style lang="scss" scoped>
.search-object-to-link {
    &__search-input {
        margin-bottom: var(--spacing-4);

        &:has(+ .compass-empty-state) {
            margin-bottom: var(--spacing-3);
        }
    }

    .search-bar-filters {
        margin-bottom: var(--spacing-2);
    }

    a[class^='search-result']::v-deep {
        padding: var(--spacing-1);

        &:hover {
            background: var(--colors-common-background-hover, #f6f6f9);
        }

        .compass-badge,
        .opportunity-status {
            flex-shrink: 0;
        }
    }

    .result-container {
        &__loader {
            color: var(--colors-common-text-standard-default, #111);
            display: flex;
            gap: 10px;
            padding: var(--spacing-1) 0;

            &-text {
                width: 100%;
            }
        }

        &__no-results {
            align-items: center;
            background: var(--colors-common-background-primary-lightest, #f5f7ff);
            border-radius: var(--spacing-0);
            color: var(--colors-common-text-standard-default, #111);
            display: flex;
            gap: var(--spacing-0);
            padding: var(--spacing-1);

            p {
                margin: 0;
            }
        }
    }
}
</style>
